import React, { useState, useRef } from 'react';
import { Container, Card } from 'react-bootstrap';
import PageTitle from '@/common/PageTitle/PageTitle';
import { useProfile, ProfileData } from '../ProfileContext';
import { getProfileUploadUrl, uploadToS3, getProfilePictureUrl } from '../services/profileService';
import { useAuth0 } from '@auth0/auth0-react';
import { buildAuth0AuthorizationParams } from '@/lib/auth0AuthorizationParams';
import { getAppStage } from '@/lib/appStage';
import { getMaHeaderInitials } from '@/utils/maHeaderUser';
import styles from './account-profile.module.scss';

const AccountProfile: React.FC = () => {
  const { profile, setProfile, isLoading } = useProfile();
  const { getAccessTokenSilently, user } = useAuth0();
  const [editMode, setEditMode] = useState(false);
  const [localProfile, setLocalProfile] = useState(profile);
  const [avatar, setAvatar] = useState<string | null>(profile.avatar);
  const [avatarKey, setAvatarKey] = useState<string | null>(null); // S3 key for the avatar
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [initialsKey, setInitialsKey] = useState(0); // Force re-render when initials change
  const initialsRef = useRef<string>('');
  const initialsSpanRef = useRef<HTMLSpanElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Helper to get token
  const getToken = async (): Promise<string> => {
    return await getAccessTokenSilently({
      authorizationParams: buildAuth0AuthorizationParams(),
    });
  };
  
  /* Initials fallback for the page avatar circle. Uses the same helper
   * as the header (`getMaHeaderInitials`) so both surfaces agree on the
   * fallback chain:
   *   1) `firstName + lastName` initials when both are present
   *   2) first two chars of `firstName` if only firstName
   *   3) Auth0 `given_name` / `family_name` / `name` claims
   *   4) first two chars of email local-part (e.g. "DE" for
   *      `demo.user@example.com`)
   *   5) literal "U" only when nothing else is available
   * The real photo (`<img src={avatar} …>`) still takes precedence
   * earlier in the render; these initials only show when no avatar /
   * avatarUrl is set. */
  const initials = React.useMemo(
    () =>
      getMaHeaderInitials(
        {
          firstName: localProfile.firstName,
          lastName: localProfile.lastName,
          email: localProfile.email,
        },
        user
      ),
    [localProfile.firstName, localProfile.lastName, localProfile.email, user]
  );
  
  // Force re-render when initials change by updating key and directly updating DOM
  React.useEffect(() => {
    if (initials && initials !== initialsRef.current) {
      console.log('[account-profile] Initials changed from', initialsRef.current, 'to', initials);
      initialsRef.current = initials;
      setInitialsKey(prev => prev + 1);
      
      // Directly update the DOM element if it exists (bypasses React reconciliation)
      if (initialsSpanRef.current) {
        console.log('[account-profile] Directly updating DOM element text to:', initials);
        initialsSpanRef.current.textContent = initials;
      }
    }
  }, [initials]);
  
  /* Sync the editable field shadow only when NOT in edit mode, so the
   * user's in-progress firstName/lastName/phone/role edits aren't blown
   * away by a background ProfileContext refresh. */
  React.useEffect(() => {
    if (!editMode) {
      setLocalProfile(profile);
    }
  }, [profile, editMode]);

  /* Sync the rendered <img src> + persisted S3 key to the latest
   * ProfileContext state regardless of edit mode. The persisted
   * DynamoDB row is the single source of truth for both the page and
   * the global header; an avatar uploaded mid-edit should appear
   * immediately and never depend on local-only UI state.
   *
   * Resolution mirrors getAvatarUrl():
   *   1. profile.avatarUrl — short-lived presigned GET URL from native M1
   *      (the only URL the browser can fetch from the private bucket).
   *   2. profile.avatar if it already looks like a URL (legacy / Auth0).
   *   3. Built from the S3 key — legacy/backward-compat fallback only;
   *      will 403 against BlockPublicAccess-on buckets.
   *
   * avatarKey is always sourced from profile.avatar (the persisted key,
   * never the presigned URL) so handleSave() PUTs the right value. */
  React.useEffect(() => {
    if (profile.avatarUrl) {
      setAvatar(profile.avatarUrl);
      setAvatarKey(profile.avatar ?? null);
    } else if (profile.avatar) {
      if (profile.avatar.startsWith('http')) {
        setAvatar(profile.avatar);
      } else {
        setAvatar(getProfilePictureUrl(profile.avatar, getAppStage()));
      }
      setAvatarKey(profile.avatar);
    } else {
      setAvatar(null);
      setAvatarKey(null);
    }
  }, [profile.avatar, profile.avatarUrl]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocalProfile((prev: ProfileData) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      await uploadAvatarFile(file);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        await uploadAvatarFile(file);
      }
    }
  };

  const uploadAvatarFile = async (file: File) => {
    try {
      setUploading(true);

      /* (1) Mint a one-shot presigned PUT URL and ship the bytes to S3
       *     directly from the browser. */
      const { uploadUrl, key } = await getProfileUploadUrl(file.type, getToken);
      await uploadToS3(file, uploadUrl);

      /* (2) Persist the new S3 key on the user's DynamoDB profile via
       *     PUT /profile so the backend record becomes the single
       *     source of truth. The PUT response includes a fresh
       *     short-lived presigned `avatarUrl` (native M1); ProfileContext
       *     stores that and any downstream consumer (this page, the
       *     global header avatar in ProfileDropdown) re-renders from it
       *     via the avatar/avatarUrl useEffect above.
       *
       *     Deliberately NO optimistic blob/URL.createObjectURL preview:
       *     per V3 acceptance, the persisted backend row must be the
       *     only renderable source. The Uploading… overlay covers the
       *     round-trip; the photo flips to the canonical presigned URL
       *     as soon as the PUT response lands. */
      await setProfile({ ...profile, avatar: key });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  /* Clear the persisted avatar. Mirrors uploadAvatarFile()'s "DynamoDB
   * is the single source of truth" approach: PUT /profile with avatar
   * set to null, and let the profile-context refresh drive the local
   * useEffect that flips this page (and the global header) back to
   * initials. We do NOT delete the underlying S3 object here — that's a
   * separate concern (lifecycle policy / cleanup job) that does not
   * affect the user-facing fix and would require additional backend +
   * IAM scope. The bucket is private (BlockPublicAccess on) so the
   * orphan object is not exposed. */
  const removeAvatarFile = async () => {
    try {
      setUploading(true);
      await setProfile({ ...profile, avatar: null });
    } catch (error) {
      console.error('Error removing avatar:', error);
      alert('Failed to remove photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleAvatarClick = () => {
    if (editMode && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSave = async () => {
    try {
      // Save profile with S3 key (not the preview URL)
      await setProfile({ ...localProfile, avatar: avatarKey });
      setEditMode(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    }
  };

  return (
  <Container fluid>
    <Card className={styles.card}>
      <Card.Body>
        <Card.Title className={styles.cardTitle}>
          <PageTitle title="Account Profile" />
        </Card.Title>
        <div className={styles.profileSection}>
          {/* avatarWrap is the positioning context for the Remove (×)
           * badge. We can't drop the badge inside .avatar because
           * .avatar uses overflow:hidden to clip the round image, which
           * would clip the badge corner too. */}
          <div className={styles.avatarWrap}>
            <div
              key={`avatar-container-${initials}-${localProfile.firstName || ''}-${localProfile.lastName || ''}-${initialsKey}`}
              className={`${styles.avatar} ${editMode ? styles.editable : ''} ${dragActive ? styles.dragActive : ''}`}
              onClick={handleAvatarClick}
              onDrop={editMode ? handleDrop : undefined}
              onDragOver={editMode ? handleDragOver : undefined}
              onDragLeave={editMode ? handleDragLeave : undefined}
            >
              {uploading ? (
                <span className={styles.uploadText}>Uploading...</span>
              ) : avatar ? (
                <img src={avatar} alt="Avatar" key={`avatar-img-${avatar}-${initials}`} />
              ) : (
                editMode ? (
                  <span className={styles.uploadText}>Upload Photo</span>
                ) : (
                  !isLoading && initials ? (
                    <span 
                      ref={initialsSpanRef}
                      key={`profile-avatar-initials-${initials}-${initialsKey}`}
                      style={{ 
                        color: '#666',
                        fontWeight: 500,
                        fontSize: '28px'
                      }}
                    >
                      {initials}
                    </span>
                  ) : (
                    <span className={styles.uploadText}>{isLoading ? 'Loading...' : 'U'}</span>
                  )
                )
              )}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className={styles.fileInput}
                onChange={handleAvatarChange}
              />
            </div>
            {/* Remove-photo affordance. Visible only in edit mode when
             * a photo is actually set (avatar resolved to a URL from
             * profile.avatar/avatarUrl). Hidden during uploading so the
             * user can't trigger a remove mid-PUT. stopPropagation is
             * critical: the parent .avatar div has onClick=open-picker. */}
            {editMode && avatar && !uploading && (
              <button
                type="button"
                className={styles.removeBadge}
                onClick={(e) => {
                  e.stopPropagation();
                  removeAvatarFile();
                }}
                aria-label="Remove photo"
                title="Remove photo"
              >
                ×
              </button>
            )}
          </div>
          <div className={styles.profileInfo}>
            <div className={styles.profileName}>
              {localProfile.firstName || localProfile.lastName 
                ? `${localProfile.firstName} ${localProfile.lastName}`.trim()
                : localProfile.email || 'User'}
            </div>
            {localProfile.role && (
              <div className={styles.profileRole}>{localProfile.role}</div>
            )}
            <div className={styles.profileEmail}>{localProfile.email}</div>
          </div>
          <div className={styles.editSection}>
            {!editMode ? (
              <a href="#" className={styles.editLink} onClick={e => { e.preventDefault(); setEditMode(true); }}><span role="img" aria-label="edit">✏️</span> Edit</a>
            ) : (
              <button
                className={styles.saveButton}
                onClick={handleSave}
              >
                💾 Save
              </button>
            )}
          </div>
        </div>
        <hr />
        <div className={styles.sectionTitle}>Personal Information</div>
        <div className={styles.infoGrid}>
          <div className={styles.infoColumn}>
            <div className={styles.fieldLabel}>First Name</div>
            {editMode ? (
              <input
                type="text"
                name="firstName"
                value={localProfile.firstName}
                onChange={handleChange}
                className={styles.inputField}
                placeholder="First Name"
              />
            ) : (
              <div className={styles.fieldValue}>{localProfile.firstName || '-'}</div>
            )}
            <div className={`${styles.fieldLabel} ${styles.spaced}`}>Phone Number</div>
            {editMode ? (
              <input
                type="text"
                name="phone"
                value={localProfile.phone}
                onChange={handleChange}
                className={styles.inputField}
                placeholder="Phone Number"
              />
            ) : (
              <div className={styles.fieldValue}>{localProfile.phone || '-'}</div>
            )}
          </div>
          <div className={styles.infoColumn}>
            <div className={styles.fieldLabel}>Last Name</div>
            {editMode ? (
              <input
                type="text"
                name="lastName"
                value={localProfile.lastName}
                onChange={handleChange}
                className={styles.inputField}
                placeholder="Last Name"
              />
            ) : (
              <div className={styles.fieldValue}>{localProfile.lastName || '-'}</div>
            )}
            <div className={`${styles.fieldLabel} ${styles.spaced}`}>Email Address</div>
            {editMode ? (
              <input
                type="email"
                name="email"
                value={localProfile.email}
                onChange={handleChange}
                className={styles.inputField}
                placeholder="Email Address"
                disabled
                title="Email address is managed by Auth0 and cannot be changed here"
              />
            ) : (
              <div className={styles.fieldValue}>{localProfile.email}</div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  </Container>
);
};

export default AccountProfile; 